import { Box, useTheme } from "@mui/material";
import type { FC } from "react";
import { GRID_RADIUS, MOTION, NEUTRAL, SELECTION_GLYPH, SURFACE } from "@/theme/tokens";

/** Tri-state of the rounded checkbox glyph. Shared discriminator across the checkbox + dropdown, so an enum
 * (not a string literal union) — call sites reference the member, never a bare string. */
export enum RoundedCheckboxState {
	On = "on",
	Off = "off",
	Indeterminate = "indeterminate",
}

type Props = {
	state: RoundedCheckboxState;
	disabled?: boolean;
	/** Animate the check/dash in (opacity+scale) as the box fills — for standalone toggles, not MUI icon slots. */
	animated?: boolean;
	/** Mark decorative when the surrounding element already conveys the state to AT (e.g. a labelled row/button). */
	"aria-hidden"?: boolean;
	/**
	 * The list option's box (`SystemRoundedCheckbox`'s `look="list"`): 4px corners and a darker resting edge, as the
	 * design system draws a checkbox inside a menu or a filter list. Off, it is the rounded control box.
	 */
	square?: boolean;
};

const { SIZE: BOX_SIZE, BORDER_WIDTH, MARK_ENTER_SCALE } = SELECTION_GLYPH;

/**
 * The single source of truth for the app's rounded tri-state checkbox glyph (18px box, 6px radius, 1.5px
 * border, border-drawn check / dash). Consumed by `SystemRoundedCheckbox` (as MUI icon slots) and by the
 * dropdown's `DropdownCheckbox` (standalone, animated) — so the two can never visually drift again.
 */
export const RoundedCheckboxBox: FC<Props> = ({
	state,
	disabled,
	animated,
	"aria-hidden": ariaHidden,
	// DEFAULT: the rounded box every caller had.
	square = false,
}) => {
	const theme = useTheme();
	// Disabled controls render in muted greys so they read as unavailable rather than active.
	const checkedColor = disabled ? NEUTRAL[500] : theme.palette.primary.main;
	const uncheckedBorder = disabled ? NEUTRAL[300] : square ? NEUTRAL[500] : NEUTRAL[400];
	const uncheckedBg = disabled ? NEUTRAL[100] : SURFACE;
	const filled = state !== RoundedCheckboxState.Off;
	const animate = animated
		? {
				transition: `background-color ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}, border-color ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
				"@media (prefers-reduced-motion: reduce)": { transition: "none" },
			}
		: {};

	return (
		<Box
			aria-hidden={ariaHidden}
			sx={{
				width: BOX_SIZE,
				height: BOX_SIZE,
				flex: "0 0 auto",
				borderRadius: square ? GRID_RADIUS.BADGE : "6px",
				boxSizing: "border-box",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				border: `${BORDER_WIDTH}px solid ${filled ? checkedColor : uncheckedBorder}`,
				backgroundColor: filled ? checkedColor : uncheckedBg,
				...animate,
			}}
		>
			{state === RoundedCheckboxState.Indeterminate ? (
				<Box component="span" sx={{ width: 9, height: 0, borderTop: `2px solid ${SURFACE}`, borderRadius: "2px" }} />
			) : (
				// Checkmark drawn from two borders; for animated standalone use it fades/scales in as the box fills,
				// as a static MUI icon slot it's simply present (on) or transparent (off).
				<Box
					component="span"
					sx={{
						width: 9,
						height: 5,
						mt: "-2px",
						borderLeft: `2px solid ${SURFACE}`,
						borderBottom: `2px solid ${SURFACE}`,
						transform: state === RoundedCheckboxState.On ? "rotate(-45deg) scale(1)" : `rotate(-45deg) scale(${MARK_ENTER_SCALE})`,
						opacity: state === RoundedCheckboxState.On ? 1 : 0,
						...(animated
							? {
									transition: `opacity ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}, transform ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
									"@media (prefers-reduced-motion: reduce)": { transition: "none" },
								}
							: {}),
					}}
				/>
			)}
		</Box>
	);
};
